import Button from "components/Common/button";
import ModalLayout from "components/Common/Modal/ModalLayout";
import useNotificationReadAllMutation from "hooks/mutations/notification/useNotificationReadAllMutation";
import useNotificationReadIdMutation from "hooks/mutations/notification/useNotificationReadIdMutation";
import useNotificationListQuery from "hooks/queries/notification/useNotificationListQuery";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getIsUser, getUserInfo } from "utils/utility";

const NotificationModal = ({
  modalClose,
  totalCount,
}: {
  modalClose: () => void;
  totalCount: number | null;
}) => {
  const navigate = useNavigate();
  const isUser = getIsUser();
  const userInfo = getUserInfo();

  const { data, fetchNextPage } = useNotificationListQuery(isUser);
  const notificationData = data?.pages.flatMap((e) => e.content);

  const { isVisible, targetRef } = useInfiniteScroll();

  const { mutate } = useNotificationReadAllMutation();

  useEffect(() => {
    if (isVisible && isUser) fetchNextPage();
  }, [isVisible]);

  return (
    <ModalLayout handleModalClose={modalClose}>
      <NotificationModalContainer>
        <NotificationTitleContainer>
          <div className="title">
            알림<span>{totalCount}</span>
          </div>

          <div className="subTitleContainer">
            <div className="dateInfo">최근 90일간 알림</div>
            <div className="allRead" onClick={() => mutate()}>
              모두 읽음
            </div>
          </div>
        </NotificationTitleContainer>

        <NotificationContentContainer>
          {notificationData && notificationData.length > 0 ? (
            <>
              {notificationData.map((e, idx) => (
                <NotificationItem key={idx} item={e} />
              ))}
            </>
          ) : (
            <div className="noNotification">
              {isUser ? (
                <>
                  <div className="noNotificationText">새소식이 없습니다.</div>
                  <div className="buttonContainer">
                    {userInfo?.userType === "HOSPITAL" && (
                      <Button
                        colorType="line"
                        className="button"
                        onClick={() => {
                          navigate("/data-room");
                          modalClose();
                        }}
                      >
                        내 병원 정보 보러가기
                      </Button>
                    )}
                    <Button
                      colorType="primary"
                      className="button"
                      onClick={() => {
                        navigate("/matching");
                        modalClose();
                      }}
                    >
                      매칭 바로가기
                    </Button>
                  </div>
                </>
              ) : (
                <div className="noNotificationText">
                  로그인 후 이용가능합니다.
                </div>
              )}
            </div>
          )}
          <div ref={targetRef} style={{ height: "50px" }}></div>
        </NotificationContentContainer>
      </NotificationModalContainer>
    </ModalLayout>
  );
};

export default NotificationModal;

const NotificationItem = ({ item }: { item: NotificationContent }) => {
  const { mutate } = useNotificationReadIdMutation();

  return (
    <NotificationItemContainer
      onClick={() => {
        if (!item.isRead) mutate(item.notificationId);
      }}
    >
      {!item.isRead && <div className="notificationDot" />}
      <div className="contentContainer">
        <div className="notificationContent">
          {!item.isRead && (
            <>
              새로운 알림 메시지 <br />
            </>
          )}
          {item.message}
        </div>
        <div className="date">{item.createdAt}</div>
      </div>
    </NotificationItemContainer>
  );
};

const NotificationModalContainer = styled.div`
  display: flex;
  padding: 2.4rem 1.2rem 1.6rem;
  box-sizing: border-box;
  border-radius: 1.6rem;
  background: #fff;
  gap: 0.8rem;
  flex-direction: column;
  width: 34.8rem;
  height: 50.6rem;
`;

const NotificationTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding: 0.8rem;

  .title {
    color: #1c1d1f;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 150%;

    & > span {
      color: #757c86;
      margin-left: 0.8rem;
      font-weight: 400;
    }
  }

  & > .subTitleContainer {
    display: flex;
    width: 100%;
    justify-content: space-between;
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 150%;

    .dateInfo {
      color: #757c86;
    }

    .allRead {
      cursor: pointer;
      color: #40444a;
      text-decoration-line: underline;
    }
  }
`;

const NotificationContentContainer = styled.div`
  display: flex;
  padding: 1.2rem;
  flex-direction: column;
  gap: 1.2rem;
  border-radius: 1.2rem;
  background: #f7faff;
  overflow-y: scroll;
  height: 100%;

  -ms-overflow-style: none; /*IE, Edge*/
  scrollbar-width: none; /*Firefox*/
  ::-webkit-scrollbar {
    display: none; /*Chrome, Safari, Opera*/
    width: 0px;
  }

  & > .noNotification {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 7.6rem;

    & > .noNotificationText {
      color: #40444a;
      font-size: 1.6rem;
      font-weight: 400;
      line-height: 150%;
    }

    & > .buttonContainer {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      box-sizing: border-box;

      & > .button {
        width: 16rem;
        padding: 1rem 1.1rem;
      }
    }
  }
`;

const NotificationItemContainer = styled.div`
  display: flex;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid #e8f0ff;
  cursor: pointer;

  & > .notificationDot {
    border-radius: 22.2rem;
    background: #053692;
    width: 0.4rem;
    height: 0.4rem;
  }

  & > .contentContainer {
    margin-left: 0.4rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 150%;

    .notificationContent {
      color: #1c1d1f;
    }

    & > .date {
      color: #848c98;
    }
  }
`;
